import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Send, Paperclip, Users, LogOut, X, Image as ImageIcon, FileText, Pencil, Camera } from "lucide-react";
import { api, formatError, fmtTime, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Avatar = ({ path, picture, name, size = 32 }) => (
  <img src={fileUrl(path) || picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "D")}&background=1a1424&color=D4AF37`} alt=""
    style={{ width: size, height: size }} className="rounded-full object-cover border border-white/10 shrink-0" />
);

export default function GroupConversation() {
  const { gid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.id;
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [gname, setGname] = useState("");
  const [savingG, setSavingG] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const gPhotoRef = useRef(null);
  const isOwner = group && group.owner === uid;

  const appendMsg = useCallback((m) => {
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
  }, []);

  const load = useCallback(() => api.get(`/groups/${gid}`).then((r) => {
    setGroup(r.data.group); setMembers(r.data.members); setMessages(r.data.messages);
  }).catch((e) => { toast.error(formatError(e.response?.data?.detail)); navigate("/friends"); }), [gid, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let alive = true; let ws;
    const connect = () => {
      ws = new WebSocket(`${base}/api/ws/chat`);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.event === "group_message" && data.group_id === gid) {
            appendMsg(data.message);
            api.post(`/groups/${gid}/read`).catch(() => {});
          }
        } catch { /* noop */ }
      };
      ws.onclose = (e) => { if (alive && e.code !== 1008) setTimeout(connect, 3000); };
    };
    connect();
    return () => { alive = false; try { ws && ws.close(); } catch { /* noop */ } };
  }, [gid, appendMsg]);

  const sendText = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true); setText("");
    try { const r = await api.post(`/groups/${gid}/messages`, { text: t }); appendMsg(r.data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); setText(t); }
    finally { setSending(false); }
  };

  const uploadAndSend = async (file) => {
    if (file.size > 25 * 1024 * 1024) { toast.error("File is larger than 25MB"); return; }
    const tid = toast.loading("Uploading…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = (await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      const ct = up.content_type || "";
      const type = ct.startsWith("image/") ? "image" : ct.startsWith("video/") ? "video" : "file";
      const r = await api.post(`/groups/${gid}/messages`, { type, media_url: up.url, media_name: up.name, media_size: up.size });
      appendMsg(r.data); toast.dismiss(tid);
    } catch (e) { toast.dismiss(tid); toast.error(formatError(e.response?.data?.detail) || "Upload failed"); }
  };

  const leave = async () => {
    try { await api.post(`/groups/${gid}/leave`); toast.success("Left group"); navigate("/friends"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveName = async () => {
    const nm = gname.trim();
    if (!nm || nm === group.name) { setGname(""); return; }
    setSavingG(true);
    try { const r = await api.patch(`/groups/${gid}`, { name: nm }); setGroup((g) => ({ ...g, name: r.data.name })); setGname(""); toast.success("Group renamed"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSavingG(false); }
  };

  const changePhoto = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Image is larger than 25MB"); return; }
    const tid = toast.loading("Uploading…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = (await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      const path = up.url.split("/api/files/")[1] || up.url;
      const r = await api.patch(`/groups/${gid}`, { avatar_path: path });
      setGroup((g) => ({ ...g, avatar_path: r.data.avatar_path }));
      toast.dismiss(tid); toast.success("Photo updated");
    } catch (e) { toast.dismiss(tid); toast.error(formatError(e.response?.data?.detail) || "Upload failed"); }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-[#0D0B14]">
      <header className="sticky top-0 z-20 flex items-center gap-3 px-4 py-3 pt-[max(env(safe-area-inset-top),0.75rem)] bg-[#0D0B14]/90 backdrop-blur-xl border-b border-white/10">
        <button onClick={() => navigate("/friends")} data-testid="group-back" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-90 transition-transform"><ChevronLeft size={20} /></button>
        <button onClick={() => setShowMembers(true)} data-testid="group-header" className="flex-1 min-w-0 text-left">
          <p className="font-display text-lg font-semibold truncate">{group?.name || "Group"}</p>
          <p className="text-[#A19DB0] text-xs">{members.length} members</p>
        </button>
        <span className="w-9 h-9 rounded-full bg-[#9D4EDD]/20 text-[#9D4EDD] flex items-center justify-center shrink-0 overflow-hidden">{group?.avatar_path ? <img src={fileUrl(group.avatar_path)} alt="" className="w-full h-full object-cover" /> : <Users size={18} />}</span>
      </header>

      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2" data-testid="group-messages">
        {messages.map((m) => {
          const mine = m.from_id === uid;
          return (
            <div key={m.id} className={`flex gap-2 ${mine ? "justify-end" : "justify-start"}`}>
              {!mine && <Avatar path={m.from_avatar} name={m.from_name} size={28} />}
              <div className={`max-w-[76%] rounded-2xl px-3 py-2 ${mine ? "bg-[#D4AF37] text-black" : "bg-white/8 border border-white/10 text-white"}`}>
                {!mine && <p className="text-[11px] font-semibold text-[#D4AF37] mb-0.5">{(m.from_name || "").split(" ")[0]}</p>}
                {m.type === "image" && <img src={m.media_url} alt="" className="rounded-lg max-h-60 mb-1" />}
                {m.type === "video" && <video src={m.media_url} controls className="rounded-lg max-h-60 mb-1 w-full" />}
                {m.type === "file" && <a href={m.media_url} target="_blank" rel="noreferrer" className={`flex items-center gap-2 underline ${mine ? "text-black" : "text-[#D4AF37]"}`}><FileText size={15} /> {m.media_name || "File"}</a>}
                {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                <p className={`text-[10px] mt-0.5 ${mine ? "text-black/60" : "text-[#A19DB0]/70"}`}>{fmtTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={sendText} className="sticky bottom-0 flex items-center gap-2 px-3 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] bg-[#0D0B14]/95 backdrop-blur-xl border-t border-white/10">
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAndSend(f); e.target.value = ""; }} data-testid="group-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="group-attach" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 active:scale-90 transition-transform"><Paperclip size={18} className="text-[#A19DB0]" /></button>
        <input value={text} onChange={(e) => setText(e.target.value)} data-testid="group-input" placeholder="Message the group"
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none placeholder:text-white/30" />
        <button type="submit" disabled={sending || !text.trim()} data-testid="group-send" className="w-10 h-10 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shrink-0 active:scale-90 transition-transform disabled:opacity-40"><Send size={18} /></button>
      </form>

      {showMembers && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end" onClick={() => setShowMembers(false)} data-testid="group-members-sheet">
          <div className="w-full max-w-md mx-auto bg-[#1a1424] border-t border-white/10 rounded-t-3xl p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">{group?.name}</h3>
              <button onClick={() => setShowMembers(false)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center"><X size={16} /></button>
            </div>
            {isOwner && (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-3 mb-4" data-testid="group-edit-panel">
                <div className="flex items-center gap-3">
                  <button onClick={() => gPhotoRef.current?.click()} data-testid="group-photo-btn" className="w-14 h-14 rounded-full bg-[#9D4EDD]/20 text-[#9D4EDD] flex items-center justify-center shrink-0 overflow-hidden relative">
                    {group?.avatar_path ? <img src={fileUrl(group.avatar_path)} alt="" className="w-full h-full object-cover" /> : <Camera size={20} />}
                    <span className="absolute bottom-0 right-0 w-5 h-5 rounded-full bg-[#D4AF37] text-black flex items-center justify-center"><Camera size={11} /></span>
                  </button>
                  <input ref={gPhotoRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) changePhoto(f); e.target.value = ""; }} data-testid="group-photo-input" />
                  <div className="flex-1 flex items-center gap-2">
                    <input value={gname} onChange={(e) => setGname(e.target.value)} placeholder={group?.name} maxLength={60} data-testid="group-rename-input"
                      className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
                    <button onClick={saveName} disabled={savingG || !gname.trim()} data-testid="group-rename-save" className="w-9 h-9 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shrink-0 disabled:opacity-40"><Pencil size={15} /></button>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                  <Avatar path={m.avatar_path} picture={m.picture} name={m.name} size={38} />
                  <p className="flex-1 font-medium truncate">{m.name}{m.id === group?.owner ? " · Admin" : ""}{m.id === uid ? " (You)" : ""}</p>
                </div>
              ))}
            </div>
            <button onClick={leave} data-testid="group-leave" className="mt-4 w-full rounded-full bg-[#ff5b5b]/15 text-[#ff8a8a] py-2.5 text-sm font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"><LogOut size={15} /> Leave group</button>
          </div>
        </div>
      )}
    </div>
  );
}
